import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
  Badge,
  Button,
  FormField,
  Input,
  Label,
  PageLineTabs,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@yeshuman/sdk'
import { useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import {
  useAddRoleCompetency,
  useCompetencyLibrary,
  type CompetencyLevel,
  type RoleDetail,
} from '../../hooks/use-roles'

const FORM_ID = 'role-competency-form'
const DEFAULT_LEVELS: CompetencyLevel[] = [
  { ordinal: 1, label: 'Developing', anchor: '' },
  { ordinal: 2, label: 'Proficient', anchor: '' },
  { ordinal: 3, label: 'Advanced', anchor: '' },
  { ordinal: 4, label: 'Expert', anchor: '' },
]

type Source = 'library' | 'new'

export function TargetLevelSelect({
  id,
  ariaLabel,
  levels,
  value,
  onChange,
  disabled,
}: {
  id?: string
  ariaLabel?: string
  levels: CompetencyLevel[]
  value: number
  onChange: (ordinal: number) => void
  disabled?: boolean
}) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))} disabled={disabled}>
      <SelectTrigger id={id} aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {levels.map((level) => (
          <SelectItem key={level.ordinal} value={String(level.ordinal)}>
            {level.ordinal}. {level.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function RoleCompetencyCanvas({
  role,
  onClose,
  onSaved,
}: {
  role: RoleDetail
  onClose: () => void
  onSaved: () => void
}) {
  const { competencies, isLoading: libraryLoading } = useCompetencyLibrary()
  const add = useAddRoleCompetency()
  const taken = useMemo(() => new Set(role.competencies.map((rc) => rc.competency.id)), [role])
  const available = useMemo(
    () => competencies.filter((c) => c.status === 'active' && !taken.has(c.id)),
    [competencies, taken],
  )

  const [source, setSource] = useState<Source>('library')
  const [competencyId, setCompetencyId] = useState('')
  const [label, setLabel] = useState('')
  const [definition, setDefinition] = useState('')
  const [skills, setSkills] = useState<string[]>([])
  const [skillDraft, setSkillDraft] = useState('')
  const [targetLevel, setTargetLevel] = useState(2)
  const [rationale, setRationale] = useState('')

  useEffect(() => {
    if (!libraryLoading && available.length === 0) setSource('new')
  }, [libraryLoading, available.length])

  const selected = available.find((c) => c.id === competencyId)
  const levels = source === 'library' && selected ? selected.levels : DEFAULT_LEVELS
  const effectiveTarget = levels.some((l) => l.ordinal === targetLevel) ? targetLevel : levels[0]?.ordinal ?? 1

  const addSkill = () => {
    const name = skillDraft.trim()
    if (name && !skills.some((s) => s.toLowerCase() === name.toLowerCase())) setSkills([...skills, name])
    setSkillDraft('')
  }

  const onSkillKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addSkill()
    }
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (source === 'library' && !selected) {
      toast.error('Choose a competency from the library')
      return
    }
    if (source === 'new' && !label.trim()) {
      toast.error('Name the competency')
      return
    }
    add.mutate(
      {
        roleId: role.id,
        competencyId: source === 'library' ? competencyId : undefined,
        newCompetency: source === 'new' ? { label: label.trim(), definition, skills } : undefined,
        targetLevel: effectiveTarget,
        rationale,
      },
      {
        onSuccess: onSaved,
        onError: (error: Error) => toast.error(error.message || 'Failed to add competency'),
      },
    )
  }

  return (
    <>
      <ArtifactCanvasBody className="min-h-0 flex-1 overflow-y-auto">
        <PageLineTabs
          value={source}
          onValueChange={(value) => setSource(value as Source)}
          ariaLabel="Competency source"
          items={[
            { value: 'library', label: 'From library' },
            { value: 'new', label: 'New competency' },
          ]}
        />
        <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-6">
          {source === 'library' ? (
            <FormField
              label="Competency"
              htmlFor={`${FORM_ID}-competency`}
              description={available.length === 0 ? 'The library has nothing left to add. Create a new one.' : selected?.definition}
            >
              <Select value={competencyId} onValueChange={setCompetencyId}>
                <SelectTrigger id={`${FORM_ID}-competency`}>
                  <SelectValue placeholder="Choose a competency" />
                </SelectTrigger>
                <SelectContent>
                  {available.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          ) : (
            <>
              <FormField label="Name *" description="Saved to the shared library so other roles can reuse it.">
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., Site safety" />
              </FormField>
              <FormField label="Definition">
                <Textarea
                  value={definition}
                  onChange={(e) => setDefinition(e.target.value)}
                  placeholder="What this competency means in practice…"
                  rows={3}
                />
              </FormField>
              <div className="flex flex-col gap-2">
                <Label htmlFor={`${FORM_ID}-skill`} className="text-sm font-medium">
                  Component skills
                </Label>
                  <div className="flex gap-2">
                    <Input
                      id={`${FORM_ID}-skill`}
                      value={skillDraft}
                      onChange={(e) => setSkillDraft(e.target.value)}
                      onKeyDown={onSkillKeyDown}
                      placeholder="e.g., Scaffold inspection"
                    />
                    <Button type="button" variant="outline" onClick={addSkill} disabled={!skillDraft.trim()}>
                      Add
                    </Button>
                  </div>
                  {skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {skills.map((skill) => (
                        <Badge key={skill} variant="secondarySoft" className="gap-1">
                          {skill}
                          <button
                            type="button"
                            aria-label={`Remove ${skill}`}
                            onClick={() => setSkills(skills.filter((s) => s !== skill))}
                          >
                            <X className="size-3" aria-hidden />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  ) : null}
              </div>
            </>
          )}
          <FormField label="Target level" htmlFor={`${FORM_ID}-target`} description="The level this role needs.">
            <TargetLevelSelect
              id={`${FORM_ID}-target`}
              levels={levels}
              value={effectiveTarget}
              onChange={setTargetLevel}
            />
          </FormField>
          <FormField label="Why it matters for this role">
            <Textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="e.g., Leads crews on multi-storey sites"
              rows={3}
            />
          </FormField>
        </form>
      </ArtifactCanvasBody>
      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={() => (document.getElementById(FORM_ID) as HTMLFormElement | null)?.requestSubmit()}
            isDirty
            isSaving={add.isPending}
            saveLabel="Add competency"
            closeDisabled={add.isPending}
          />
        }
      />
    </>
  )
}
